
import { supabase } from '@/integrations/supabase/client';
import { mapSupabaseDataToSubscription } from '@/utils/dbUtils';
import { Subscription } from '@/types/subscription';

export interface SubscriptionFetchResult {
  subscriptions: Subscription[];
  debugInfo: any;
}

export async function fetchUserSubscriptions(userEmail: string): Promise<SubscriptionFetchResult> {
  const debug: any = { userEmail, steps: [] };
  
  try {
    debug.steps.push("Starting subscription fetch");
    
    // Step 1: Get ALL subscriptions to see what's in the database
    const { data: allSubscriptions, error: allError } = await supabase
      .from('subscriptions')
      .select('*');
    
    debug.allSubscriptionsCount = allSubscriptions?.length || 0;
    debug.steps.push(`Found ${debug.allSubscriptionsCount} total subscriptions in database`);
    
    if (allSubscriptions && allSubscriptions.length > 0) {
      debug.sampleEmails = allSubscriptions.slice(0, 5).map(sub => ({
        id: sub.id,
        email: sub.email,
        customer_name: sub.customer_name,
        business_name: sub.business_name || 'None',
        is_business: sub.is_business,
        active: sub.active,
        status: sub.status
      }));
      debug.steps.push("Sample subscription data captured");
      
      // Check for business subscriptions specifically
      const businessSubs = allSubscriptions.filter(sub => sub.is_business === true);
      debug.totalBusinessSubscriptions = businessSubs.length;
      debug.businessSubscriptionDetails = businessSubs.map(sub => ({
        id: sub.id,
        customer_name: sub.customer_name,
        business_name: sub.business_name,
        is_business: sub.is_business,
        email: sub.email
      }));
      debug.steps.push(`Found ${businessSubs.length} business subscriptions in database`);
      
      // ✨ NEW: Log exactly what business data looks like in raw form
      console.log("=== RAW BUSINESS SUBSCRIPTION DATA FROM SUPABASE ===");
      businessSubs.forEach((sub, index) => {
        console.log(`Business Sub ${index + 1}:`, {
          id: sub.id,
          customer_name: sub.customer_name,
          business_name: sub.business_name,
          is_business: sub.is_business,
          is_business_type: typeof sub.is_business,
          email: sub.email,
          complete_raw_object: sub
        });
      });
      console.log("=== END RAW BUSINESS SUBSCRIPTION DATA ===");
      
      // Check for any subscription with similar email
      const emailMatches = allSubscriptions.filter(sub => 
        sub.email && sub.email.toLowerCase().includes(userEmail.toLowerCase())
      );
      debug.emailMatches = emailMatches.length;
      debug.steps.push(`Found ${emailMatches.length} subscriptions with similar email`);
      
      if (emailMatches.length > 0) {
        debug.matchingSubscriptions = emailMatches.map(sub => ({
          id: sub.id,
          email: sub.email,
          customer_name: sub.customer_name,
          business_name: sub.business_name || 'None',
          is_business: sub.is_business,
          active: sub.active,
          status: sub.status
        }));
        
        // ✨ NEW: Log the user's matching subscriptions with business data details
        console.log("=== USER'S MATCHING SUBSCRIPTIONS RAW DATA ===");
        emailMatches.forEach((sub, index) => {
          console.log(`User Sub ${index + 1}:`, {
            id: sub.id,
            customer_name: sub.customer_name,
            email: sub.email,
            is_business: sub.is_business,
            is_business_type: typeof sub.is_business,
            business_name: sub.business_name,
            business_name_type: typeof sub.business_name,
            complete_raw_object: sub
          });
        });
        console.log("=== END USER'S MATCHING SUBSCRIPTIONS RAW DATA ===");
      }
    }
    
    const { finalData, selectedStrategy } = await executeSearchStrategies(userEmail, debug);
    
    if (!finalData || finalData.length === 0) {
      debug.steps.push("No subscriptions found for user in database");
      return { subscriptions: [], debugInfo: debug };
    }
    
    debug.finalDataCount = finalData.length;
    debug.steps.push(`Final data count: ${debug.finalDataCount}`);
    debug.selectedStrategy = selectedStrategy;
    
    debug.rawFinalData = finalData.map(sub => ({
      id: sub.id,
      email: sub.email,
      customer_name: sub.customer_name,
      business_name: sub.business_name,
      is_business: sub.is_business,
      multiple_days: sub.multiple_days,
      active: sub.active,
      status: sub.status
    }));
    
    // ✨ NEW: Log the final data that will be mapped with complete business details
    console.log("=== FINAL DATA BEFORE MAPPING (DETAILED BUSINESS CHECK) ===");
    finalData.forEach((sub, index) => {
      console.log(`Final Sub ${index + 1} - BEFORE MAPPING:`, {
        id: sub.id,
        customer_name: sub.customer_name,
        email: sub.email,
        is_business: sub.is_business,
        is_business_value: sub.is_business,
        is_business_type: typeof sub.is_business,
        is_business_strict_check: sub.is_business === true,
        business_name: sub.business_name,
        business_name_type: typeof sub.business_name,
        raw_subscription_object: JSON.stringify(sub, null, 2)
      });
    });
    console.log("=== END FINAL DATA BEFORE MAPPING ===");
    
    // Show ALL subscriptions (including cancelled ones) - don't filter them out
    debug.allSubscriptionsCount = finalData.length;
    debug.steps.push(`Showing all subscriptions (including cancelled): ${debug.allSubscriptionsCount}`);
    
    console.log("=== BEFORE MAPPING ===");
    console.log("Raw data from database:", finalData);
    
    const subscriptions = finalData.map(mapSupabaseDataToSubscription);
    
    console.log("=== AFTER MAPPING ===");
    console.log("Mapped subscriptions:", subscriptions);
    console.log("Business subscriptions found:", subscriptions.filter(s => s.isBusiness));
    
    // ✨ NEW: Detailed comparison of each subscription before and after mapping
    console.log("=== BEFORE/AFTER MAPPING COMPARISON ===");
    finalData.forEach((rawSub, index) => {
      const mappedSub = subscriptions[index];
      console.log(`Subscription ${index + 1} Comparison:`, {
        before: {
          id: rawSub.id,
          is_business: rawSub.is_business,
          business_name: rawSub.business_name,
          customer_name: rawSub.customer_name
        },
        after: {
          id: mappedSub.id,
          isBusiness: mappedSub.isBusiness,
          businessName: mappedSub.businessName,
          customerName: mappedSub.customerName
        },
        mapping_lost_business: rawSub.is_business === true && mappedSub.isBusiness !== true
      });
    });
    console.log("=== END BEFORE/AFTER MAPPING COMPARISON ===");
    
    debug.mappedSubscriptionsCount = subscriptions.length;
    debug.mappedSubscriptions = subscriptions.map(sub => ({
      id: sub.id,
      customerName: sub.customerName,
      email: sub.email,
      businessName: sub.businessName,
      isBusiness: sub.isBusiness,
      active: sub.active,
      status: sub.status
    }));
    debug.businessSubscriptionsInMapped = subscriptions.filter(s => s.isBusiness).length;
    debug.steps.push(`Successfully mapped ${subscriptions.length} subscriptions (including ${debug.businessSubscriptionsInMapped} business subscriptions)`);
    
    debug.steps.push("Fetch operation completed");
    
    return { subscriptions, debugInfo: debug };
    
  } catch (error) {
    debug.steps.push(`Catch error: ${error}`);
    console.error("Failed to fetch user subscriptions:", error);
    throw error;
  }
}

async function executeSearchStrategies(userEmail: string, debug: any) {
  // Step 2: Try multiple search strategies
  const searchStrategies = [
    { name: 'exact_match', query: supabase.from('subscriptions').select('*').eq('email', userEmail) },
    { name: 'case_insensitive', query: supabase.from('subscriptions').select('*').ilike('email', userEmail) },
    { name: 'trimmed_exact', query: supabase.from('subscriptions').select('*').eq('email', userEmail.trim()) },
    { name: 'partial_match', query: supabase.from('subscriptions').select('*').ilike('email', `%${userEmail}%`) }
  ];
  
  debug.searchResults = {};
  
  for (const strategy of searchStrategies) {
    const { data, error } = await strategy.query;
    debug.searchResults[strategy.name] = {
      count: data?.length || 0,
      error: error?.message || null,
      data: data?.map(sub => ({
        id: sub.id,
        email: sub.email,
        customer_name: sub.customer_name,
        business_name: sub.business_name || 'None',
        is_business: sub.is_business,
        active: sub.active,
        status: sub.status
      })) || []
    };
    debug.steps.push(`${strategy.name}: found ${data?.length || 0} results`);
  }
  
  // Step 3: Use the best strategy that returned results
  let finalData = null;
  let finalError = null;
  let selectedStrategy = null;
  
  for (const strategy of searchStrategies) {
    if (debug.searchResults[strategy.name].count > 0) {
      const { data, error } = await strategy.query;
      finalData = data;
      finalError = error;
      selectedStrategy = strategy.name;
      debug.steps.push(`Using strategy: ${strategy.name}`);
      break;
    }
  }
  
  if (finalError) {
    debug.steps.push(`Final query error: ${finalError.message}`);
    console.error("Error fetching user subscriptions:", finalError);
    throw finalError;
  }
  
  return { finalData, selectedStrategy };
}
